import React, { useEffect } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  hideCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  hideCloseButton = false
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !hideCloseButton) {
        onClose();
      }
    };

    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen) {
        const target = e.target as HTMLElement;
        // Only handle Enter for input fields and select elements (not textareas - allow Enter for multiline)
        if ((target.tagName === 'INPUT' || target.tagName === 'SELECT') && target.getAttribute('type') !== 'submit') {
          // Find the modal container by traversing up the DOM tree
          let modal = target.closest('[data-modal-container]') as HTMLElement;
          if (!modal) return;
          
          e.preventDefault();
          
          // Get all focusable elements within the modal
          const focusableElements = modal.querySelectorAll(
            'input:not([disabled]):not([type="hidden"]):not([type="submit"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([type="submit"]), [tabindex]:not([tabindex="-1"])'
          );
          
          const focusableArray = Array.from(focusableElements) as HTMLElement[];
          const currentIndex = focusableArray.indexOf(target);
          
          if (currentIndex !== -1 && currentIndex < focusableArray.length - 1) {
            // Focus next element
            focusableArray[currentIndex + 1].focus();
          } else if (currentIndex === focusableArray.length - 1) {
            // If last element, wrap to first
            focusableArray[0]?.focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleEnter);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleEnter);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, hideCloseButton]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-lg',
    lg: 'w-full max-w-2xl',
    xl: 'w-full max-w-4xl',
    full: 'w-full max-w-[95vw]'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur effect */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-all duration-300"
        onClick={hideCloseButton ? undefined : onClose}
      />
      
      {/* Modal Container */}
      <div
        data-modal-container
        className={`relative ${sizeClasses[size]} max-h-[90vh] flex flex-col transform rounded-2xl bg-white dark:bg-gray-800 shadow-2xl transition-all duration-300 scale-100 animate-in slide-in-from-bottom-4 fade-in-0`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-6 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-t-2xl">
          <div className="flex-1 pr-4">
            {typeof title === 'string' ? (
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            ) : (
              title
            )}
          </div>
          {!hideCloseButton && (
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Close modal"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {children}
          </div>
        </div>

        {/* Sticky Footer */}
        {footer && (
          <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-700 px-6 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
